import { ChangeEvent, useCallback, useMemo } from 'react'
import { Trans, useLingui } from '@lingui/react/macro'

import Input from './Input'

type Props = {
  value?: number | null
  onChange: (duration: number) => void
  invalid?: boolean
  readOnly?: boolean
  name?: string
}

export default function DurationInput({
  value,
  onChange,
  name,
  ...rest
}: Props) {
  const { t } = useLingui()

  const currentValue = Math.round((value ?? 0) / 60_000)

  const hours = useMemo(() => {
    return Math.floor(currentValue / 60)
  }, [currentValue])

  const minutes = useMemo(() => {
    return currentValue % 60
  }, [currentValue])

  const onChangeMinutes = useCallback(
    (newMinutes: number) => {
      if (newMinutes < 0) onChange(0)
      else onChange(newMinutes * 60_000)
    },
    [onChange]
  )

  const onUpdateMinutes = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      event.preventDefault()

      let newMinutes = parseInt(event.target.value)

      if (isNaN(newMinutes)) newMinutes = 0

      // We compare with -1 here so that they can actually count down the hours with the minute "Down Button"
      if (newMinutes < -1) newMinutes = 0

      onChangeMinutes(hours * 60 + newMinutes)
    },
    [hours, onChangeMinutes]
  )

  const onUpdateHours = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      event.preventDefault()

      let newHours = parseInt(event.target.value)

      if (isNaN(newHours)) newHours = 0
      if (newHours < 0) newHours = 0

      onChangeMinutes(newHours * 60 + minutes)
    },
    [minutes, onChangeMinutes]
  )

  return (
    <div className="flex flex-row items-center gap-2">
      <Input
        className="w-20"
        {...rest}
        value={hours.toString()}
        onChange={onUpdateHours}
        name={`${name} Hours`}
        placeholder={t({
          comment: 'Placeholder for the hour input in a Duration Input',
          message: 'e.g.: 1'
        })}
        type="number"
      />
      <Trans>Hours</Trans>
      <Input
        className="w-20"
        {...rest}
        value={minutes.toString()}
        onChange={onUpdateMinutes}
        name={`${name} Minutes`}
        placeholder={t({
          comment: 'Placeholder for the minutes input in a Duration Input',
          message: 'e.g.: 30'
        })}
        type="number"
      />
      <Trans>Minutes</Trans>
    </div>
  )
}
